import { useEffect, useState } from "react";
import { formatTime } from "../../functions";
import { TABLE_HEAD_PROPS } from "../../constants";
import type { IUserModalProps, IUserTableProps, User } from "../../types";
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TablePagination,
  TableContainer,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../redux";
import { selectImages, selectPermissions, switchUserStatus } from "../Dashboard/DashboardSlice";
import { CustomModal } from "../CompletionPage/CustomModal";
import moment from "moment";
import UserModal from "../UserModal";
import { selectIsAuthentication } from "../../AppSlice";

export default function UsersTable(props: IUserTableProps) {
  const { page, numberOfEntries, users, filteredUsers, styles, handleSetSelectedUser, handleChangePage } = props;

  const currUserimages = useAppSelector(selectImages);
  const isCurrUserAuthentication = useAppSelector(selectIsAuthentication);

  const dispatch = useAppDispatch();

  const permissions = useAppSelector(selectPermissions);
  const [open, setModal] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [expandedRequests, setExpandedRequests] = useState<{
    [key: number]: IUserModalProps;
  }>({});
  const [currUserId, setCurrUserId] = useState<number | null>(null);

  const handleExpandRequest = async (user: User) => {
    if (expandedRequests[user.id]) {
      const newExpandedRequests = { ...expandedRequests };
      delete newExpandedRequests[user.id];
      setExpandedRequests(newExpandedRequests);
    } else {
      setCurrUserId(user.id);
      handleSetSelectedUser(user);
    }
  };

  useEffect(() => {
    if (currUserId) {
      const newExpandedRequests = { ...expandedRequests };
      newExpandedRequests[currUserId as number] = {
        selectedUser: user,
        images: currUserimages,
        isAuthentication: isCurrUserAuthentication,
      };
      setExpandedRequests(newExpandedRequests);
      setCurrUserId(null);
    }
  }, [currUserimages]);

  const handleSwitchStatus = async (user: any) => {
    await dispatch(switchUserStatus(user));
    handleChangePage(null, page);
  };

  return (
    <Card sx={{ ...styles.cardRoot, width: "100%" }}>
      <CustomModal modal={open} handleClose={() => setModal(false)} type={"warning"} user={user} />
      {!users.length && !filteredUsers.length ? (
        "Loading..."
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {TABLE_HEAD_PROPS.map((tableHeadProp) => (
                  <TableCell key={tableHeadProp.id} align="center">
                    <Typography sx={{ fontWeight: "bold" }}>{tableHeadProp.label}</Typography>
                  </TableCell>
                ))}

                <TableCell align="center"></TableCell>
                {permissions?.qa && (
                  <>
                    <TableCell align="center"></TableCell>
                    <TableCell align="center"></TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {(filteredUsers.length ? filteredUsers : users).map((user, index) => (
                <>
                  <TableRow
                    sx={{
                      ":hover": {
                        backgroundColor: "#999",
                      },
                      backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#DFDFDF",
                    }}
                    key={user.id}
                    tabIndex={-1}
                  >
                    <TableCell align="center">{user.id}</TableCell>

                    <TableCell align="center">{user.registrationNumber}</TableCell>

                    <TableCell align="center">{user.currentStatus}</TableCell>

                    <TableCell align="center">{formatTime(user.birthDate)}</TableCell>

                    <TableCell align="center">
                      {user.UserInfo.country ? user.UserInfo.country : "Undetermined"}
                    </TableCell>

                    <TableCell align="center">
                      {user.UserInfo.documentType
                        ? user.UserInfo.documentType === "UNDEFINED"
                          ? "Undefined"
                          : user.UserInfo.documentType
                        : "Undefined"}
                    </TableCell>

                    <TableCell align="center">{user.email ? user.email : null}</TableCell>

                    <TableCell align="center">{user.phoneNumber}</TableCell>

                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "20px",
                          color:
                            user.UserInfo.status === "approved" || user.UserInfo.status === "authed"
                              ? "green"
                              : "black",
                        }}
                      >
                        {user.UserInfo.status.toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{moment(user.UserInfo.createdAt).format("DD-MM-YY")}</TableCell>
                    <TableCell align="center">{user.requestId ? "SP" : "Paneem"}</TableCell>

                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        onClick={() => {
                          handleExpandRequest(user);
                        }}
                      >
                        {expandedRequests[user.id] ? "CLOSE" : "VIEW"}
                      </Button>
                    </TableCell>
                    {permissions?.qa ? (
                      <>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setModal(true);
                              setUser(user);
                              // deleteCase(user);
                            }}
                          >
                            reset
                          </Button>
                        </TableCell>
                        {(user.currentStatus === "approved" || user.currentStatus === "authed") && (
                          <TableCell align="center">
                            <Button variant="outlined" onClick={() => handleSwitchStatus(user)}>
                              Auth Request
                            </Button>
                          </TableCell>
                        )}
                      </>
                    ) : null}
                  </TableRow>
                  {expandedRequests[user.id] && (
                    <TableRow>
                      <TableCell colSpan={14}>
                        <UserModal
                          selectedUser={user}
                          images={expandedRequests[user.id].images}
                          isAuthentication={expandedRequests[user.id].isAuthentication}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <TablePagination
        rowsPerPage={10}
        component="div"
        count={filteredUsers.length ? filteredUsers.length : numberOfEntries ? numberOfEntries : 0}
        rowsPerPageOptions={[10]}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={() => {}}
        backIconButtonProps={{
          disabled: !!filteredUsers.length || page === 0,
        }}
        nextIconButtonProps={{
          disabled: !!filteredUsers.length || users.length !== 10,
        }}
      />
    </Card>
  );
}
